/**
 * Client-side image compression utility.
 * Resizes images to a max dimension and compresses them as JPEG.
 * Returns a Promise that resolves to a compressed Blob.
 */

const DEFAULT_MAX_WIDTH = 1920;
const DEFAULT_MAX_HEIGHT = 1920;
const DEFAULT_QUALITY = 0.8;

/**
 * Compress an image file client-side before upload.
 * @param {File} file - The original image file
 * @param {Object} options
 * @param {number} options.maxWidth - Max width in pixels (default 1920)
 * @param {number} options.maxHeight - Max height in pixels (default 1920)
 * @param {number} options.quality - JPEG quality 0-1 (default 0.8)
 * @returns {Promise<Blob>} - A compressed JPEG blob
 */
export const compressImage = (file, options = {}) => {
  return new Promise((resolve, reject) => {
    // Skip compression for non-image files
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    // Skip compression for GIFs (to preserve animation)
    if (file.type === 'image/gif') {
      resolve(file);
      return;
    }

    const maxWidth = options.maxWidth || DEFAULT_MAX_WIDTH;
    const maxHeight = options.maxHeight || DEFAULT_MAX_HEIGHT;
    const quality = options.quality !== undefined ? options.quality : DEFAULT_QUALITY;

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas toBlob failed'));
            return;
          }
          // Preserve original filename but change extension to .jpg
          const originalName = file.name || 'image';
          const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
          const newFileName = `${nameWithoutExt}.jpg`;

          // Create a File from the blob so it has a name
          const compressedFile = new File([blob], newFileName, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          console.log(
            `[compressImage] ${file.name}: ${(file.size / 1024).toFixed(1)}KB → ${(blob.size / 1024).toFixed(1)}KB (${((blob.size / file.size) * 100).toFixed(0)}%)`
          );

          resolve(compressedFile);
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for compression'));
    };

    img.src = objectUrl;
  });
};

/**
 * Compress multiple image files in parallel.
 * @param {File[]} files - Array of files
 * @param {Object} options - Compression options
 * @returns {Promise<(File|Blob)[]>} - Array of compressed files
 */
export const compressImages = async (files, options = {}) => {
  const results = await Promise.all(
    files.map((file) =>
      compressImage(file, options).catch((err) => {
        console.warn(`[compressImages] Failed to compress ${file.name}:`, err.message);
        return file; // Return original on failure
      })
    )
  );
  return results;
};
